import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '💛',
  number: '35 Classes',
  title: 'JAZZ TECHNIQUE',
  subtitle: 'The technical foundation your jazz students need — structured, progressive, and ready to teach.',
  problem: 'Jazz technique is one of the most requested styles — and one of the hardest to keep fresh and technically purposeful week after week. This block gives you 35 fully structured sessions that build alignment, rhythm, sharpness, and performance quality progressively — without repeating yourself.',
  bullets: ['How to build jazz classes that develop real technical skill — not just run combinations', 'How to train isolations, weight shifts, and rhythm with clear progression', 'How to teach turns, kicks, and leaps with safer preparation and proper cues', 'How to develop performance quality and confidence without losing technical focus', 'Teaching cues, common corrections, and progressions written into every session'],
  result: 'Your jazz students develop cleaner technique, sharper timing, and stronger performance quality — and you walk in with a clear plan every single time.',
  popular: true
}, {
  icon: '💙',
  number: '35 Classes',
  title: 'CONTEMPORARY TECHNIQUE',
  subtitle: 'Grounded, expressive, and completely structured — from breath to floor work.',
  problem: 'Contemporary technique demands a specific kind of sequencing — breath, weight, release, and intention need to work together. This block gives you 35 sessions built around real contemporary principles so every class feels purposeful, embodied, and clearly taught.',
  bullets: ['How to structure contemporary classes that develop genuine movement quality — not just shapes', 'How to teach breath-led movement, release, and suspension with clear language', 'How to guide floor work, level changes, and controlled falls with safety built in', 'How to develop emotional intention and musical interpretation without losing technical direction', 'Progressions for beginners and intermediate dancers written into every class'],
  result: 'Your contemporary students move with more depth, presence, and technical understanding — and you teach each session with complete confidence and clarity.',
  popular: false
}, {
  icon: '🟠',
  number: '30 Classes',
  title: 'MUSICAL THEATRE DANCE',
  subtitle: 'Character, storytelling, stage presence, and jazz-based technique — all in one structured lesson.',
  problem: 'Musical theatre dance is where technique meets performance — and it requires a completely different kind of class structure. This block gives you 30 sessions that combine jazz foundations with character, expression, and stage readiness so your students feel truly prepared for the stage, the audition room, and the spotlight.',
  bullets: ['How to teach musical theatre classes that build both technical skill and performance confidence', 'How to develop character walks, gesture, expression, and storytelling through movement', 'How to connect lyric interpretation with physical choices in a structured class setting', 'How to prepare students for audition-style combinations and ensemble performance', 'Teaching cues and performance corrections written into every session'],
  result: 'Your musical theatre students perform with presence, authenticity, and technical grounding — and every class feels intentional, engaging, and professionally structured.',
  popular: false
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-286">
      <div className="max-w-6xl mx-auto" data-id="element-287">
        <div className="text-center mb-16" data-id="element-288">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-289">
            📚 100 Ready-to-Teach Dance Classes
          </h2>
          <p className="text-xl text-gold-light" data-id="element-290">
            3 Styles · 40+ Weeks of Fresh Content · Nothing to Build From
            Scratch
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-291">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-dark-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-292">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-293">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-294">
                <span className="text-4xl" data-id="element-295">{tomo.icon}</span>
                <div data-id="element-296">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-297">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-298">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-299">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-300">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-301">
                  What does it solve in your teaching?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-302">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-303">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-304">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-305" />
                    <span className="text-gray-200 text-sm" data-id="element-306">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-307">
                <span className="text-gold font-bold block mb-1" data-id="element-308">Result:</span>
                <p className="text-white text-sm" data-id="element-309">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}